import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.utiliti.ng";

class AtmCardService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api/atm-card`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers['x-auth-token'] = `${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        return response.data;
      },
      (error) => {
        console.log(error);
        if (error.response?.status === 401) {
          // Handle unauthorized access
          //   localStorage.removeItem('token');  
          //   localStorage.removeItem('user');
          //   window.location.href = '/login';
        }
        return Promise.reject(error.response?.data || error);
      }
    );
  }

  // Get all ATM card requests (Admin only)
  async getAllRequests(params = {}) {
    try {
      const response = await this.api.get('/admin/requests', { params });
      console.log(response);
      return response;
    } catch (error) {
      console.error('Error fetching all requests:', error);
      throw error;
    }
  }

  // Get request by ID
  async getRequestById(requestId) {
    try {
      const response = await this.api.get(`/admin/requests/${requestId}`);
      return response;
    } catch (error) {
      console.error('Error fetching request by ID:', error);
      throw error;
    }
  }

  // Update request status and details
  async updateRequest(requestId, updateData) {
    try {
      const response = await this.api.put(`/admin/requests/${requestId}`, updateData);
      return response;
    } catch (error) {
      console.error('Error updating request:', error);
      throw error;
    }
  }

  // Send notification to user
  async sendNotification(requestId, notificationData) {
    try {
      const response = await this.api.post(`/admin/requests/${requestId}/notify`, notificationData);
      return response;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  }

  // Get request statistics
  async getRequestStats() {
    try {
      const response = await this.api.get('/admin/stats');
      return response;
    } catch (error) {
      console.error('Error fetching request stats:', error);
      throw error;
    }
  }

  // Bulk update requests
  async bulkUpdateRequests(updates) {
    try {
      const response = await this.api.put('/admin/requests/bulk', { updates });
      return response;
    } catch (error) {
      console.error('Error bulk updating requests:', error);
      throw error;
    }
  }

  // Export requests data
  async exportRequests(format = 'csv', filters = {}) {
    try {
      const response = await this.api.get('/admin/requests/export', {
        params: { format, ...filters },
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      console.error('Error exporting requests:', error);
      throw error;
    }
  }

  // Get delivery tracking information
  async getDeliveryTracking(requestId) {
    try {
      const response = await this.api.get(`/admin/requests/${requestId}/tracking`);
      return response;
    } catch (error) {
      console.error('Error fetching delivery tracking:', error);
      throw error;
    }
  }

  // Update delivery tracking
  async updateDeliveryTracking(requestId, trackingData) {
    try {
      const response = await this.api.put(`/admin/requests/${requestId}/tracking`, trackingData);
      return response;
    } catch (error) {
      console.error('Error updating delivery tracking:', error);
      throw error;
    }
  }

  // Get user's card requests (for user dashboard)
  async getUserRequests() {
    try {
      const response = await this.api.get('/user/requests');
      return response;
    } catch (error) {
      console.error('Error fetching user requests:', error);
      throw error;
    }
  }

  // Get user's card request status
  async getUserRequestStatus() {
    try {
      const response = await this.api.get('/user/request/status');
      return response;
    } catch (error) {
      console.error('Error fetching user request status:', error);
      throw error;
    }
  }

  // Request new card (for user)
  async requestCard(deliveryAddress) {
    try {
      const response = await this.api.post('/user/request', { deliveryAddress });
      return response;
    } catch (error) {
      console.error('Error requesting card:', error);
      throw error;
    }
  }

  // Connect card after delivery
  async connectCard(cardNumber) {
    try {
      const response = await this.api.post('/user/connect', { cardNumber });
      return response;
    } catch (error) {
      console.error('Error connecting card:', error);
      throw error;
    }
  }

  // Get card settings
  async getCardSettings() {
    try {
      const response = await this.api.get('/user/settings');
      return response;
    } catch (error) {
      console.error('Error fetching card settings:', error);
      throw error;
    }
  }

  // Get user's connected card
  async getUserCard() {
    try {
      const response = await this.api.get('/user/card');
      return response;
    } catch (error) {
      console.error('Error fetching user card:', error);
      throw error;
    }
  }

  // Update card status (block/unblock)
  async updateCardStatus(cardId, status) {
    try {
      const response = await this.api.put(`/user/card/${cardId}/status`, { status });
      return response;
    } catch (error) {
      console.error('Error updating card status:', error);
      throw error;
    }
  }

  // Change card PIN
  async changeCardPIN(cardId, newPin) {
    try {
      const response = await this.api.put(`/user/card/${cardId}/pin`, { newPin });
      return response;
    } catch (error) {
      console.error('Error changing card PIN:', error);
      throw error;
    }
  }

  // Get card transactions
  async getCardTransactions(cardId, params = {}) {
    try {
      const response = await this.api.get(`/user/card/${cardId}/transactions`, { params });
      return response;
    } catch (error) {
      console.error('Error fetching card transactions:', error);
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.api.get('/health');
      return response;
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  }
}

export const atmCardService = new AtmCardService();